import { useEffect, useState } from "react";
import MarkdownEditor from "@/components/MarkdownEditor";
import { Button } from "@/components/ui/Button";
import { api } from "@/services/api";
import { useToast } from "@/context/ToastContext";
import type { UserProfile } from "@/types";

type TabId = "experience" | "preferences" | "cv_template" | "cl_template";

const TABS: { id: TabId; label: string }[] = [
  { id: "experience",   label: "Experience Vault" },
  { id: "preferences",  label: "Preferences" },
  { id: "cv_template",  label: "CV Base Template" },
  { id: "cl_template",  label: "Cover Letter Base Template" },
];

export default function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile>({
    experience_data: "",
    preferences: "",
    cv_base_template: "",
    cl_base_template: "",
  });
  const { show: showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("experience");

  useEffect(() => {
    api
      .get<UserProfile>("/api/profile")
      .then(setProfile)
      .catch((err) => showToast("error", err.detail || "Failed to load profile"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await api.put<UserProfile>("/api/profile", profile);
      setProfile(updated);
      showToast("success", "Profile saved.");
    } catch (err: any) {
      showToast("error", err.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="p-6 text-slate-400">Loading profile…</p>;

  return (
    <div className="max-w-[800px] mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">My Profile</h1>
      <p className="text-slate-500 dark:text-slate-400 mb-6">
        Your profile is the knowledge base used to tailor all documents.
      </p>

      <form onSubmit={handleSave} className="flex flex-col gap-6">
        {/* Tab bar */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 -mb-2" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={[
                "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 whitespace-nowrap",
                activeTab === tab.id
                  ? "border-accent text-accent"
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600",
              ].join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab panels */}
        <div
          id="tabpanel-experience"
          role="tabpanel"
          hidden={activeTab !== "experience"}
        >
          <MarkdownEditor
            label="Experience Vault"
            value={profile.experience_data}
            onChange={(v) => setProfile((p) => ({ ...p, experience_data: v }))}
            placeholder="Paste your full professional history, education, skills and projects here…"
          />
        </div>
        <div
          id="tabpanel-preferences"
          role="tabpanel"
          hidden={activeTab !== "preferences"}
        >
          <MarkdownEditor
            label="Preferences"
            value={profile.preferences}
            onChange={(v) => setProfile((p) => ({ ...p, preferences: v }))}
            placeholder="Describe your target roles, salary expectations, culture preferences, location…"
          />
        </div>
        <div
          id="tabpanel-cv_template"
          role="tabpanel"
          hidden={activeTab !== "cv_template"}
        >
          <MarkdownEditor
            label="CV Base Template"
            value={profile.cv_base_template}
            onChange={(v) => setProfile((p) => ({ ...p, cv_base_template: v }))}
            placeholder="Define your CV structure with Markdown headings and {{placeholders}}…"
          />
        </div>
        <div
          id="tabpanel-cl_template"
          role="tabpanel"
          hidden={activeTab !== "cl_template"}
        >
          <MarkdownEditor
            label="Cover Letter Base Template"
            value={profile.cl_base_template}
            onChange={(v) => setProfile((p) => ({ ...p, cl_base_template: v }))}
            placeholder="Define your cover letter structure…"
          />
        </div>

        <div>
          <Button type="submit" variant="primary" loading={saving} disabled={saving}>
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </div>
  );
}
